<template>
  <div class="curvilinear-motion-viz">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex><strong>公式：</strong>详见下方原理说明</div>
    </div>

    <div class="canvas-box">
      <svg width="100%" height="300" viewBox="0 0 400 300">
        <!-- 轨迹历史 -->
        <path :d="pathData" fill="none" stroke="#444" stroke-width="1" stroke-dasharray="2,2" />
        
        <!-- 物体 -->
        <g :transform="`translate(${pos.x}, ${pos.y})`">
          <circle r="8" fill="#00d2ff" shadow="0 0 10px #00d2ff" />
          
          <!-- 速度矢量 v (切线方向) -->
          <line x1="0" y1="0" :x2="vVec.x" :y2="vVec.y" stroke="#2ed573" stroke-width="3" marker-end="url(#arr-v)" />
          <text :x="vVec.x + 5" :y="vVec.y" fill="#2ed573" font-size="12">v</text>

          <!-- 合力矢量 F (指向轨迹内侧) -->
          <line x1="0" y1="0" :x2="fVec.x" :y2="fVec.y" stroke="#ff4757" stroke-width="3" marker-end="url(#arr-f)" />
          <text :x="fVec.x + 5" :y="fVec.y" fill="#ff4757" font-size="12">F合</text>
        </g>
      </svg>
    </div>

    <div class="controls">
      <div class="slider-row">
        <span>改变合力方向 (F合):</span>
        <el-slider v-model="forceAngle" :min="60" :max="120" :step="1" size="mini"></el-slider>
      </div>
      <p class="desc">合外力的方向总是指向运动轨迹的<strong>凹侧</strong>。</p>
      <el-button size="mini" type="primary" @click="reset" style="width:100%; margin-top:10px;">重新开始</el-button>
    </div>

    <defs>
      <marker id="arr-v" markerWidth="6" markerHeight="4" refX="6" refY="2" orient="auto"> <polygon points="0 0, 6 2, 0 4" fill="#2ed573" /> </marker>
      <marker id="arr-f" markerWidth="6" markerHeight="4" refX="6" refY="2" orient="auto"> <polygon points="0 0, 6 2, 0 4" fill="#ff4757" /> </marker>
    </defs>
  </div>
</template>

<script>
export default {
  name: 'CurvilinearMotionViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      forceAngle: 90, // 与速度方向的夹角
      pos: { x: 50, y: 150 },
      v: { x: 4, y: 0 },
      history: [],
      running: true
    }
  },
  computed: {
    vVec() {
      // 这里的 vVec 仅仅是为了可视化，长度固定
      const mag = Math.sqrt(this.v.x**2 + this.v.y**2);
      return { x: (this.v.x / mag) * 40, y: (this.v.y / mag) * 40 };
    },
    fVec() {
      // 合力方向：将速度向量旋转 forceAngle 度
      const angleRad = this.forceAngle * Math.PI / 180;
      const vx = this.vVec.x;
      const vy = this.vVec.y;
      // 旋转矩阵
      return {
        x: vx * Math.cos(angleRad) - vy * Math.sin(angleRad),
        y: vx * Math.sin(angleRad) + vy * Math.cos(angleRad)
      };
    },
    pathData() {
      if (this.history.length < 2) return "";
      return "M " + this.history.map(p => `${p.x} ${p.y}`).join(" L ");
    }
  },
  mounted() {
    this.animate();
  },
  beforeDestroy() {
    this.running = false;
  },
  methods: {
    reset() {
      this.pos = { x: 50, y: 150 };
      this.v = { x: 3, y: 0 };
      this.history = [];
    },
    animate() {
      if (!this.running) return;
      
      this.history.push({ ...this.pos });
      if (this.history.length > 100) this.history.shift();

      // 根据合力改变速度方向
      // 简单模拟：合力产生加速度
      const angleRad = this.forceAngle * Math.PI / 180; // eslint-disable-line no-unused-vars
      const speed = Math.sqrt(this.v.x**2 + this.v.y**2);
      const currentAngle = Math.atan2(this.v.y, this.v.x);
      
      // 力的方向 = currentAngle + angleRad
      // 只是改变方向，不改变速率 (匀速圆周运动的变体)
      const newAngle = currentAngle + (0.02 * (this.forceAngle > 90 ? 1 : -1));
      
      this.v.x = Math.cos(newAngle) * speed;
      this.v.y = Math.sin(newAngle) * speed;
      
      this.pos.x += this.v.x;
      this.pos.y += this.v.y;

      if (this.pos.x > 400 || this.pos.x < 0 || this.pos.y > 300 || this.pos.y < 0) {
        this.reset();
      }
      
      requestAnimationFrame(this.animate);
    }
  }
}
</script>

<style scoped lang="scss">
.curvilinear-motion-viz {
  width: 100%;
  color: #fff;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.viz-header {
  display: flex;
  justify-content: space-between;
  padding: 10px;
  background: rgba(255, 255, 255, 0.05);
  .title { color: #00d2ff; font-weight: bold; }
  .stats { font-size: 0.8rem; color: #aaa; }
}
.canvas-box { background: #0a0a0a; border-radius: 8px; overflow: hidden; }
.controls {
  padding: 15px;
  background: rgba(255, 255, 255, 0.02);
  .slider-row { display: flex; align-items: center; gap: 15px; span { font-size: 0.85rem; } .el-slider { flex: 1; } }
  .desc { font-size: 0.8rem; color: #888; margin-top: 10px; text-align: center; }
}
</style>
